import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, type ConfirmationResult } from 'firebase/auth';
import { getFirebaseApp } from './config';

export function formatFirebasePhoneAuthError(err: unknown): string {
  const code = typeof err === 'object' && err && 'code' in err ? String((err as { code?: string }).code) : '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'this site';

  switch (code) {
    case 'auth/captcha-check-failed':
    case 'auth/unauthorized-domain':
      return `Phone verification is blocked for ${hostname}. Add this domain in Firebase Console → Authentication → Authorized domains.`;
    case 'auth/invalid-phone-number':
      return 'Enter a valid phone number with country code (e.g. +919876543210).';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    default:
      break;
  }

  if (err instanceof Error && err.message) return err.message;
  return 'Failed to send OTP. Please try again.';
}

export function formatPhoneNumber(raw: string) {
  const trimmed = raw.trim();
  if (trimmed.startsWith('+')) return trimmed;
  if (trimmed.length === 10) return `+91${trimmed}`;
  throw new Error('Please specify country code (e.g. +919999999999)');
}

export async function startPhoneSignIn(
  phoneNumber: string,
): Promise<{ mode: 'web'; confirmation: ConfirmationResult }> {
  const formatted = formatPhoneNumber(phoneNumber);
  const app = getFirebaseApp();
  if (!app) throw new Error('Firebase is not configured on the client.');
  const auth = getAuth(app);
  if (process.env.NODE_ENV === 'development') {
    auth.settings.appVerificationDisabledForTesting = true;
  }

  const container = document.getElementById('recaptcha-container');
  if (container) container.innerHTML = '';

  const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
  try {
    const confirmation = await signInWithPhoneNumber(auth, formatted, recaptchaVerifier);
    return { mode: 'web', confirmation };
  } catch (err) {
    recaptchaVerifier.clear();
    throw new Error(formatFirebasePhoneAuthError(err));
  }
}

export async function completePhoneSignIn(
  state: { mode: 'web'; confirmation: ConfirmationResult },
  otpCode: string,
): Promise<string> {
  const result = await state.confirmation.confirm(otpCode);
  return result.user.getIdToken();
}
